import React, { useState, useEffect } from 'react';
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, Mail } from "lucide-react";
function HeaderMenuConsumer({
  isMobile = false,
  onItemClick
}: {
  isMobile?: boolean;
  onItemClick?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="38450" className={`flex ${isMobile ? 'flex-col gap-4' : 'gap-1'}`}>
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-9 w-20 bg-muted/10 animate-pulse rounded-sm" />)}
      </div>;
  }
  const processedMenuItems = menuItems.filter((item, index, self) => index === self.findIndex(t => t.href === item.href || t.label === item.label)).filter(item => item.label !== "Get a Quote");
  if (isMobile) {
    return <nav className="flex flex-col gap-6">
        {processedMenuItems.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col">
            <Link to={item.href || "#"} className="text-2xl font-serif font-bold uppercase tracking-widest text-white hover:text-primary hover:shadow-sm transition-all" onClick={onItemClick}>
              {item.label}
            </Link>
          </div>)}
      </nav>;
  }
  return <NavigationMenu viewport={false} className="justify-start">
      <NavigationMenuList className="justify-start gap-6">
        {processedMenuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i} className={i === processedMenuItems.length - 1 ? "mr-4" : ""}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group inline-flex h-9 w-max items-center justify-start rounded-sm bg-transparent px-4 py-2 text-sm font-serif font-bold uppercase tracking-widest text-white transition-all hover:bg-primary/5 hover:text-primary hover:shadow-sm focus:bg-primary/5 focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-primary/5 data-[state=open]:bg-primary/5">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 dark bg-linear-to-b from-background via-background/98 to-background/95 text-black ${isScrolled ? 'bg-background/80 backdrop-blur-lg border-b border-white/10 shadow-xl' : 'border-b border-white/5'}`}>
      <div className="container ml-0 max-w-7xl px-8 h-20 flex items-center justify-center gap-12">
        
        <div className="flex-shrink-0">
          <Button variant="ghost" className="p-0 h-auto hover:bg-transparent" asChild>
            <Link to="/">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/simprotile_logo.png" alt="SimProTiling" className="h-8 w-auto object-contain brightness-0 invert" />
            </Link>
          </Button>
        </div>

        
        <div className="hidden lg:flex items-center justify-start px-4">
          <MenuProvider menu_id="16">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        
        <div className="hidden lg:flex items-center gap-6">
          <Link to="tel:07467562688" className="flex items-center gap-2 text-xs font-serif font-bold uppercase tracking-widest text-black hover:text-primary/80 hover:shadow-sm transition-all">
            <Phone className="h-3.5 w-3.5 text-black" />
            <span>Simi: 0746 756 2688</span>
          </Link>
          <Link to="mailto:simprotile@gmail.com" className="flex items-center gap-2 text-xs font-serif font-bold uppercase tracking-widest text-black hover:text-primary hover:shadow-sm transition-all">
            <Mail className="h-3.5 w-3.5 text-black" />
            <span>Email Us</span>
          </Link>
          <Link to="mailto:Simprotile@yahoo.com" className="flex items-center gap-2 text-xs font-serif font-bold uppercase tracking-widest text-black hover:text-primary hover:shadow-sm transition-all">
            <Mail className="h-3.5 w-3.5 text-black" />
            <span>Yahoo Mail</span>
          </Link>
        </div>

        
        <div className="lg:hidden">
          <Button variant="ghost" size="icon" className="text-black hover:text-primary/80 hover:bg-primary/10" onClick={() => setIsMobileMenuOpen(true)}>
            <Menu className="h-6 w-6 text-black" />
            <span className="sr-only">Open menu</span>
          </Button>
        </div>
      </div>

      
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-xl lg:hidden flex flex-col">
          <div className="container ml-0 px-4 h-20 flex items-center justify-start gap-8 border-b border-white/10">
            <Button variant="ghost" className="p-0 h-auto hover:bg-transparent" asChild>
              <Link to="/">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/simprotile_logo.png" alt="SimProTiling" className="h-8 w-auto object-contain brightness-0 invert" />
              </Link>
            </Button>
            <Button variant="ghost" size="icon" className="text-white hover:text-primary/80 hover:bg-primary/10" onClick={() => setIsMobileMenuOpen(false)}>
              <X className="h-6 w-6 text-black" />
              <span className="sr-only">Close menu</span>
            </Button>
          </div>
          
          <div className="flex-1 overflow-y-auto py-12 px-4 flex flex-col items-start gap-12">
            <MenuProvider menu_id="16">
              <HeaderMenuConsumer isMobile onItemClick={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>

            <div className="flex flex-col items-start gap-6 mt-auto pb-12">
              <div className="w-12 h-[1px] bg-primary/20" />
              <Link to="tel:07467562688" className="flex items-center gap-3 text-lg font-serif font-bold uppercase tracking-widest text-black hover:text-primary/80 hover:shadow-sm transition-all">
                <Phone className="h-5 w-5 text-black" />
                <span>0746 756 2688</span>
              </Link>
              <Link to="mailto:simprotile@gmail.com" className="flex items-center gap-3 text-lg font-serif font-bold uppercase tracking-widest text-black hover:text-primary hover:shadow-sm transition-all">
                <Mail className="h-5 w-5 text-black" />
                <span>simprotile@gmail.com</span>
              </Link>
              <Link to="mailto:Simprotile@yahoo.com" className="flex items-center gap-3 text-lg font-serif font-bold uppercase tracking-widest text-black hover:text-primary hover:shadow-sm transition-all">
                <Mail className="h-5 w-5 text-black" />
                <span>Simprotile@yahoo.com</span>
              </Link>
            </div>
          </div>
        </div>}
    </header>;
}